/**
 * Tenant custom fields — calculated metrics, custom dimensions, relabeling and
 * metric visibility. Interpreted from the flank-20 dashboard experience
 * (FLA2-639/640/644/648) onto the flank / ribeye-api reporting stack.
 *
 * Two deliberate differences from flank-20:
 *
 * 1. Definitions live in the dashboard's own `config` blob rather than a
 *    tenant-wide table, because ribeye-api's `/custom-dashboards` already
 *    persists `config` and nothing here needs a migration. Tenant-wide sharing
 *    is a later step, not a different shape.
 * 2. Fields are derived from the rows `postReport` already returns instead of
 *    being compiled into SQL. Every calculated metric is arithmetic over
 *    canonical metrics in the same row, and every custom dimension buckets a
 *    dimension value that is already selected, so nothing needs the warehouse.
 */
import { CANONICAL_METRICS, type MetricFormat } from './canonicalMetrics';

const METRIC_DEFS = new Map(CANONICAL_METRICS.map(m => [m.key, m]));

/** Prefix marking a key as tenant-defined, so pickers can badge it. */
export const CUSTOM_FIELD_PREFIX = 'custom:';

export const isCustomFieldKey = (key: string): boolean => key.startsWith(CUSTOM_FIELD_PREFIX);

export interface CalculatedMetricDef {
  /** Stable id, always prefixed. */
  key: string;
  label: string;
  format: MetricFormat;
  /** Arithmetic over canonical metric keys, e.g. `spend / store_visits`. */
  formula: string;
}

export interface DimensionBucket {
  label: string;
  /** Matched case-insensitively against the base dimension value. */
  values: string[];
}

export interface CustomDimensionDef {
  key: string;
  label: string;
  /** Canonical dimension this buckets, e.g. `channel`. */
  field: string;
  /** First match wins, so order is meaningful. */
  buckets: DimensionBucket[];
  /** Applied to any value no bucket claims. */
  fallback: string;
}

export interface TenantFields {
  metrics: CalculatedMetricDef[];
  dimensions: CustomDimensionDef[];
  /** Canonical key -> tenant label. */
  labels: Record<string, string>;
  /** Canonical metric keys hidden from pickers and widgets. */
  hiddenMetrics: string[];
}

export const EMPTY_TENANT_FIELDS: TenantFields = {
  metrics: [],
  dimensions: [],
  labels: {},
  hiddenMetrics: [],
};

/** Metric keys a formula may reference. */
export const KNOWN_METRIC_KEYS: string[] = CANONICAL_METRICS.map(m => m.key);

// ── formula parsing ────────────────────────────────────────────────────────
// Recursive descent over + - * / and parentheses. Deliberately not `eval` or
// `new Function`: formulas are tenant-authored and end up in a saved dashboard,
// so they must never reach a JS execution context.

type Token = { type: 'num'; value: number } | { type: 'id'; value: string } | { type: 'op'; value: string };

const tokenize = (input: string): Token[] => {
  const tokens: Token[] = [];
  let i = 0;
  while (i < input.length) {
    const ch = input[i];
    if (/\s/.test(ch)) {
      i += 1;
    } else if ('+-*/()'.includes(ch)) {
      tokens.push({ type: 'op', value: ch });
      i += 1;
    } else if (/[0-9.]/.test(ch)) {
      let num = '';
      while (i < input.length && /[0-9.]/.test(input[i])) num += input[i++];
      const value = Number(num);
      if (Number.isNaN(value)) throw new Error(`"${num}" is not a number`);
      tokens.push({ type: 'num', value });
    } else if (/[a-zA-Z_]/.test(ch)) {
      let id = '';
      while (i < input.length && /[a-zA-Z0-9_]/.test(input[i])) id += input[i++];
      tokens.push({ type: 'id', value: id });
    } else {
      throw new Error(`Unexpected character "${ch}"`);
    }
  }
  return tokens;
};

type Node =
  | { kind: 'num'; value: number }
  | { kind: 'id'; value: string }
  | { kind: 'bin'; op: string; left: Node; right: Node };

const parse = (tokens: Token[]): Node => {
  let pos = 0;
  const peek = () => tokens[pos];

  const parsePrimary = (): Node => {
    const token = peek();
    if (!token) throw new Error('Formula ends unexpectedly');
    if (token.type === 'num') {
      pos += 1;
      return { kind: 'num', value: token.value };
    }
    if (token.type === 'id') {
      pos += 1;
      return { kind: 'id', value: token.value };
    }
    if (token.value === '(') {
      pos += 1;
      const inner = parseExpression();
      const close = peek();
      if (!close || close.type !== 'op' || close.value !== ')') throw new Error('Missing closing bracket');
      pos += 1;
      return inner;
    }
    if (token.value === '-') {
      // Unary minus, expressed as 0 - x so the evaluator stays binary-only.
      pos += 1;
      return { kind: 'bin', op: '-', left: { kind: 'num', value: 0 }, right: parsePrimary() };
    }
    throw new Error(`Unexpected "${token.value}"`);
  };

  const parseTerm = (): Node => {
    let left = parsePrimary();
    for (;;) {
      const token = peek();
      if (!token || token.type !== 'op' || (token.value !== '*' && token.value !== '/')) return left;
      pos += 1;
      left = { kind: 'bin', op: token.value, left, right: parsePrimary() };
    }
  };

  function parseExpression(): Node {
    let left = parseTerm();
    for (;;) {
      const token = peek();
      if (!token || token.type !== 'op' || (token.value !== '+' && token.value !== '-')) return left;
      pos += 1;
      left = { kind: 'bin', op: token.value, left, right: parseTerm() };
    }
  }

  const ast = parseExpression();
  if (pos !== tokens.length) throw new Error(`Unexpected "${tokens[pos] && (tokens[pos] as Token).value}"`);
  return ast;
};

export interface FormulaCheck {
  ok: boolean;
  error?: string;
  /** Metric keys the formula reads, for dependency display. */
  references: string[];
}

const collectIds = (node: Node, out: Set<string>): void => {
  if (node.kind === 'id') out.add(node.value);
  if (node.kind === 'bin') {
    collectIds(node.left, out);
    collectIds(node.right, out);
  }
};

/**
 * Validate a formula against the metrics it may reference. Returns rather than
 * throws — the dialog renders the message inline under the field.
 */
export const validateFormula = (formula: string, knownKeys: string[] = KNOWN_METRIC_KEYS): FormulaCheck => {
  const trimmed = formula.trim();
  if (!trimmed) return { ok: false, error: 'Enter a formula.', references: [] };
  let ast: Node;
  try {
    ast = parse(tokenize(trimmed));
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Invalid formula', references: [] };
  }
  const ids = new Set<string>();
  collectIds(ast, ids);
  const references = [...ids];
  const unknown = references.filter(id => !knownKeys.includes(id));
  if (unknown.length) {
    return { ok: false, error: `Unknown metric: ${unknown.join(', ')}`, references };
  }
  if (!references.length) {
    return { ok: false, error: 'Reference at least one metric.', references };
  }
  return { ok: true, references };
};

const evaluate = (node: Node, row: Record<string, unknown>): number | null => {
  if (node.kind === 'num') return node.value;
  if (node.kind === 'id') {
    const raw = row[node.value];
    const value = typeof raw === 'string' ? Number(raw) : raw;
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }
  const left = evaluate(node.left, row);
  const right = evaluate(node.right, row);
  if (left === null || right === null) return null;
  switch (node.op) {
    case '+':
      return left + right;
    case '-':
      return left - right;
    case '*':
      return left * right;
    case '/':
      // A zero denominator is normal in reporting (no visits yet), not an error.
      return right === 0 ? null : left / right;
    default:
      return null;
  }
};

/** Evaluate a formula against one report row. Null when any input is missing. */
export const evaluateFormula = (formula: string, row: Record<string, unknown>): number | null => {
  try {
    return evaluate(parse(tokenize(formula)), row);
  } catch {
    return null;
  }
};

// ── custom dimensions ──────────────────────────────────────────────────────

/** Bucket a raw dimension value. First matching bucket wins. */
export const resolveBucket = (value: unknown, dimension: CustomDimensionDef): string => {
  const raw = value == null ? '' : String(value).trim().toLowerCase();
  for (const bucket of dimension.buckets) {
    if (bucket.values.some(v => v.trim().toLowerCase() === raw)) return bucket.label;
  }
  return dimension.fallback;
};

// ── applying to report rows ────────────────────────────────────────────────

/**
 * Add every tenant field to the rows `postReport` returned. Pure: callers get a
 * new array and the response stays untouched.
 */
export const applyTenantFields = <T extends Record<string, unknown>>(
  rows: T[],
  fields: TenantFields = EMPTY_TENANT_FIELDS,
): T[] => {
  if (!rows?.length) return rows ?? [];
  if (!fields.metrics.length && !fields.dimensions.length) return rows;
  return rows.map(row => {
    const next: Record<string, unknown> = { ...row };
    for (const metric of fields.metrics) {
      next[metric.key] = evaluateFormula(metric.formula, row);
    }
    for (const dimension of fields.dimensions) {
      next[dimension.key] = resolveBucket(row[dimension.field], dimension);
    }
    return next as T;
  });
};

// ── picker options ─────────────────────────────────────────────────────────

export interface FieldOption {
  value: string;
  label: string;
  custom?: boolean;
  /** Present on calculated metrics, so pickers can check what the formula reads. */
  formula?: string;
}

/**
 * Canonical metrics plus tenant ones, with hidden metrics removed and tenant
 * relabeling applied. This is what a metric picker should render.
 */
export const mergeMetricOptions = (
  canonical: FieldOption[],
  fields: TenantFields = EMPTY_TENANT_FIELDS,
): FieldOption[] => {
  const hidden = new Set(fields.hiddenMetrics);
  const base = canonical
    .filter(opt => !hidden.has(opt.value))
    .map(opt => ({ ...opt, label: fields.labels[opt.value] ?? opt.label }));
  return [...base, ...fields.metrics.map(m => ({ value: m.key, label: m.label, custom: true, formula: m.formula }))];
};

/** Canonical dimensions plus tenant ones, with relabeling applied. */
export const mergeDimensionOptions = (
  canonical: FieldOption[],
  fields: TenantFields = EMPTY_TENANT_FIELDS,
): FieldOption[] => [
  ...canonical.map(opt => ({ ...opt, label: fields.labels[opt.value] ?? opt.label })),
  ...fields.dimensions.map(d => ({ value: d.key, label: d.label, custom: true })),
];

// ── persistence ────────────────────────────────────────────────────────────

const TENANT_FIELDS_CONFIG_KEY = 'tenantFields';

/** Read tenant fields out of a dashboard `config`, tolerating older dashboards. */
export const readTenantFields = (config: Record<string, unknown> | null | undefined): TenantFields => {
  const raw = config?.[TENANT_FIELDS_CONFIG_KEY] as Partial<TenantFields> | undefined;
  if (!raw) return EMPTY_TENANT_FIELDS;
  return {
    metrics: Array.isArray(raw.metrics) ? raw.metrics : [],
    dimensions: Array.isArray(raw.dimensions) ? raw.dimensions : [],
    labels: raw.labels && typeof raw.labels === 'object' ? raw.labels : {},
    hiddenMetrics: Array.isArray(raw.hiddenMetrics) ? raw.hiddenMetrics : [],
  };
};

/** Merge tenant fields back into a dashboard `config` without dropping siblings. */
export const writeTenantFields = (
  config: Record<string, unknown> | null | undefined,
  fields: TenantFields,
): Record<string, unknown> => ({ ...(config ?? {}), [TENANT_FIELDS_CONFIG_KEY]: fields });

// ── grouping by a custom dimension ─────────────────────────────────────────

const toNumber = (raw: unknown): number => {
  const value = typeof raw === 'string' ? Number(raw) : raw;
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
};

/**
 * The formula behind a derived metric. Some catalog entries append a prose note
 * — "spend / clicks (null when clicks = 0)" — which is documentation, not part
 * of the expression.
 */
const recomputeFormula = (key: string): string | null => {
  const derivedFrom = METRIC_DEFS.get(key)?.derivedFrom;
  if (!derivedFrom) return null;
  return derivedFrom.split(' (')[0].trim() || null;
};

/**
 * Whether a metric may be added across rows.
 *
 * Counts and plain currency are additive. Anything derived is not: adding two
 * CTRs, or two CPCs, produces a number that means nothing. An unknown key is
 * treated as additive, which is what a raw fact column would be.
 */
export const isAdditiveMetric = (key: string): boolean => {
  const def = METRIC_DEFS.get(key);
  if (!def) return true;
  if (def.derivedFrom) return false;
  return def.format === 'count' || def.format === 'currency';
};

/**
 * Re-aggregate rows after bucketing. Bucketing collapses several base values
 * into one label, so the rows must be summed or the widget shows the same
 * bucket several times with un-added metrics.
 *
 * Calculated metrics are recomputed from the summed inputs rather than summed
 * themselves — adding two ratios together is meaningless (CPV of $2 and $3 is
 * not $5), so the formula is re-evaluated against the aggregated row.
 */
export const aggregateByCustomDimension = <T extends Record<string, unknown>>(
  rows: T[],
  dimension: CustomDimensionDef,
  metricKeys: string[],
  calculated: CalculatedMetricDef[] = [],
  /**
   * Extra columns to keep as part of the grouping key. A pivot groups by its row
   * dimension *and* its column dimension, so collapsing on the bucket alone
   * would throw the columns away and leave one row per bucket.
   */
  extraGroupBy: string[] = [],
): Record<string, unknown>[] => {
  if (!rows?.length) return [];
  const calculatedKeys = new Set(calculated.map(m => m.key));
  const requested = metricKeys.filter(k => !calculatedKeys.has(k));
  // Only additive metrics may be added across rows. A derived one is recomputed
  // from the summed inputs below; anything else is averaged.
  const summable = requested.filter(isAdditiveMetric);
  const derived = requested.filter(k => !isAdditiveMetric(k));
  // Derived metrics are recomputed from their own inputs, which the caller may
  // not have asked for — sum those too so the recompute has something to read.
  const inputs = new Set<string>();
  for (const key of derived) {
    const formula = recomputeFormula(key);
    if (formula) for (const ref of validateFormula(formula).references) inputs.add(ref);
  }
  const summedKeys = [...new Set([...summable, ...inputs])];

  const buckets = new Map<string, { row: Record<string, unknown>; count: number; sums: Map<string, number> }>();

  for (const row of rows) {
    const label = resolveBucket(row[dimension.field], dimension);
    // \u0000 cannot appear in a dimension value, so it is a safe separator.
    const key = [label, ...extraGroupBy.map(k => String(row[k] ?? ''))].join('\u0000');
    let entry = buckets.get(key);
    if (!entry) {
      const seed: Record<string, unknown> = { [dimension.key]: label, [dimension.field]: label };
      for (const k of extraGroupBy) seed[k] = row[k];
      entry = { row: seed, count: 0, sums: new Map() };
      for (const key of summedKeys) entry.row[key] = 0;
      buckets.set(key, entry);
    }
    entry.count += 1;
    for (const key of summedKeys) {
      entry.row[key] = (entry.row[key] as number) + toNumber(row[key]);
    }
    // Averaging fallback for a non-additive metric with no formula to recompute.
    for (const key of derived) {
      entry.sums.set(key, (entry.sums.get(key) ?? 0) + toNumber(row[key]));
    }
  }

  const aggregated: Record<string, unknown>[] = [];
  for (const { row, count, sums } of buckets.values()) {
    for (const key of derived) {
      const formula = recomputeFormula(key);
      // Recompute where the catalog tells us how; otherwise average, which is
      // wrong-but-bounded rather than a meaningless sum of ratios.
      row[key] = formula ? evaluateFormula(formula, row) : count ? (sums.get(key) ?? 0) / count : null;
    }
    // Drop inputs the caller never asked for, so the shape matches the request.
    for (const key of summedKeys) if (!requested.includes(key)) delete row[key];
    for (const metric of calculated) row[metric.key] = evaluateFormula(metric.formula, row);
    aggregated.push(row);
  }
  return aggregated;
};
